import { log, storageManager } from "@app/app";
import { useCallback, useEffect, useMemo, useState } from "react";

import { RestorableStateTypes } from "../constants/RestoredStateTypes";
import { StorageKeys } from "../constants/StorageKeys";
import { ICheckoutSessionState } from "../types";
import { isValidActiveCheckoutSessionState } from "../utils/checkoutSessionUtils";

export interface IUseCheckoutSessionStatePersistanceResult {
    restoreState: () => Promise<IRestoredStateResult | undefined>;
    persistState: (state: ICheckoutSessionState) => Promise<void>;
    clearState: () => Promise<void>;
    isRestoring: boolean;
    isPersisting: boolean;
}

export interface IUseCheckoutSessionStatePersistanceProps {
    visitorId: string;
    activePropertyId: string;
    bookingHash?: string;
}

export interface IRestoredStateResult {
    type: RestorableStateTypes;
    state?: ICheckoutSessionState;
}

export const useCheckoutSessionStatePersistance = ({
    visitorId,
    activePropertyId,
    bookingHash,
}: IUseCheckoutSessionStatePersistanceProps): IUseCheckoutSessionStatePersistanceResult => {
    const [isPersisting, setIsPersisting] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);

    useEffect(() => {
        if (isRestoring) {
            console.warn("IS RESTORING PERSTISTED CHECKOUT SESSION STATE");
        }
    }, [isRestoring]);

    const restoreState = useCallback(async (): Promise<IRestoredStateResult | undefined> => {
        setIsRestoring(true);

        const result = await storageManager
            .getGlobalSession()
            .get<ICheckoutSessionState>(StorageKeys.CheckoutSessionState)
            .then((storageState) => {
                const hasValidActiveCheckoutSessionInStorage = isValidActiveCheckoutSessionState(storageState);
                const doesBookingHashMatch = !bookingHash || !storageState?.booking_hash || storageState?.booking_hash === bookingHash;

                setIsRestoring(false);

                if (hasValidActiveCheckoutSessionInStorage) {
                    if (!doesBookingHashMatch) {
                        return {
                            type: RestorableStateTypes.Mismatch,
                            state: storageState,
                        };
                    }

                    return {
                        type: RestorableStateTypes.Restorable,
                        state: storageState,
                    };
                }

                return {
                    type: RestorableStateTypes.New,
                };
            })
            .catch((error) => {
                log.error("Failed to restore checkout session state:", error);

                setIsRestoring(false);

                return undefined;
            });

        return result;
    }, [activePropertyId, visitorId, bookingHash, setIsRestoring]);

    const persistState = useCallback(
        async (state: ICheckoutSessionState): Promise<void> => {
            setIsPersisting(true);

            await storageManager.getGlobalSession().set(StorageKeys.CheckoutSessionState, state);

            setIsPersisting(false);
        },
        [setIsPersisting]
    );

    const clearState = useCallback(async (): Promise<void> => {
        await storageManager.getGlobalSession().clear(StorageKeys.CheckoutSessionState);
    }, []);

    return useMemo(
        () => ({ restoreState, persistState, clearState, isPersisting, isRestoring }),
        [restoreState, persistState, clearState, isPersisting, isRestoring]
    );
};
